/**
 * LoadingSpinner Component
 *
 * Displays a loading indicator with optional label.
 * Uses Fluent UI's Spinner component.
 */

import { Spinner, SpinnerProps, Text, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXL,
  },
  fullPage: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 1000,
  },
  label: {
    color: tokens.colorNeutralForeground2,
  },
});

export interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: SpinnerProps['size'];
  /** Optional label text */
  label?: string;
  /** Whether to display as full page overlay */
  fullPage?: boolean;
  /** Additional CSS class */
  className?: string;
}

export function LoadingSpinner({
  size = 'medium',
  label,
  fullPage = false,
  className,
}: LoadingSpinnerProps) {
  const styles = useStyles();

  const containerClasses = [
    styles.container,
    fullPage ? styles.fullPage : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} role="status" aria-live="polite">
      <Spinner size={size} />
      {label && (
        <Text className={styles.label} size={300}>
          {label}
        </Text>
      )}
    </div>
  );
}
